<template>
    <ModalOverlay v-if="show" @click.stop>
        <ModalContent @click.stop>
            <ModalHeader>
                <ModalTitle>{{ isEdit ? '담당자 수정' : '담당자 추가' }}</ModalTitle>
                <CloseButton @click="closeModal">&times;</CloseButton>
            </ModalHeader>

            <ModalBody>
                <FormContainer>
                    <FormRow>
                        <FormGroup>
                            <FormLabel required>담당자 아이디</FormLabel>
                            <FormInput v-model="formData.userId" placeholder="아이디를 입력하세요" required
                                :class="{ 'blurred': isTestUser }" />
                        </FormGroup>
                        <FormGroup>
                            <FormLabel :required="!isEdit">담당자 비밀번호</FormLabel>
                            <FormInput v-model="formData.password" type="password"
                                :placeholder="isEdit ? '변경시에만 입력하세요' : '비밀번호를 입력하세요'" :required="!isEdit"
                                :class="{ 'blurred': isTestUser }" />
                            <FormHint v-if="isEdit">비밀번호를 변경하지 않으려면 비워두세요</FormHint>
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <FormLabel required>담당자명</FormLabel>
                            <FormInput v-model="formData.name" placeholder="이름을 입력하세요" required
                                :class="{ 'blurred': isTestUser }" />
                        </FormGroup>
                        <FormGroup>
                            <FormLabel>부서</FormLabel>
                            <FormSelect v-model="formData.department">
                                <option value="">부서를 선택하세요</option>
                                <option value="대표">대표</option>
                                <option value="사원">사원</option>
                                <option value="영업">영업</option>
                            </FormSelect>
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <FormLabel>담당자 이메일</FormLabel>
                            <FormInput v-model="formData.email" type="email" placeholder="이메일을 입력하세요"
                                :class="{ 'blurred': isTestUser }" />
                        </FormGroup>
                        <FormGroup>
                            <FormLabel>담당자 전화번호</FormLabel>
                            <FormInput v-model="formData.phone" placeholder="전화번호를 입력하세요"
                                :class="{ 'blurred': isTestUser }" />
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <FormLabel>은행명</FormLabel>
                            <FormSelect v-model="formData.bank_name">
                                <option value="">은행을 선택하세요</option>
                                <option value="농협">농협</option>
                                <option value="국민">국민</option>
                                <option value="신한">신한</option>
                                <option value="하나">하나</option>
                                <option value="우리">우리</option>
                                <option value="기업">기업</option>
                                <option value="산업">산업</option>
                                <option value="새마을금고">새마을금고</option>
                                <option value="씨티">씨티</option>
                                <option value="수협">수협</option>
                                <option value="신협">신협</option>
                                <option value="우체국">우체국</option>
                                <option value="광주">광주</option>
                                <option value="전북">전북</option>
                                <option value="대구">대구</option>
                                <option value="부산">부산</option>
                                <option value="경남">경남</option>
                                <option value="제주">제주</option>
                                <option value="케이뱅크">케이뱅크</option>
                            </FormSelect>
                        </FormGroup>
                        <FormGroup>
                            <FormLabel>계좌번호</FormLabel>
                            <FormInput v-model="formData.bank_number" placeholder="- 제외하고 입력하세요"
                                :class="{ 'blurred': isTestUser }" />
                        </FormGroup>
                        <FormGroup>
                            <FormLabel>예금주명</FormLabel>
                            <FormInput v-model="formData.bank_username" placeholder="예금주명을 입력하세요"
                                :class="{ 'blurred': isTestUser }" />
                        </FormGroup>
                    </FormRow>
                </FormContainer>
            </ModalBody>

            <ModalFooter>
                <CancelButton @click="closeModal">취소</CancelButton>
                <!-- 수정 모달일 때만 삭제 버튼 표시 -->
                <DeleteButton v-if="isEdit" @click="deleteManager">삭제</DeleteButton>
                <SaveButton @click="saveManager">
                    {{ isEdit ? '수정하기' : '추가하기' }}
                </SaveButton>
            </ModalFooter>
        </ModalContent>
    </ModalOverlay>
</template>

<script>
import * as ManagerModalCss from '@/assets/styles/manage/modal/ManagerModalCss.js';

export default {
    name: 'ManagerModal',

    components: {
        ...ManagerModalCss
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        isEdit: {
            type: Boolean,
            default: false
        },
        managerData: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            formData: {
                id: null,
                userId: '',
                password: '',
                name: '',
                department: '',
                email: '',
                phone: '',
                bankInfo: '',
                bank_name: '',
                bank_number: '',
                bank_username: ''
            }
        };
    },

    watch: {
        show(newVal) {
            if (newVal) {
                this.initializeForm();
                this.addEscapeKeyListener();
            } else {
                this.removeEscapeKeyListener();
            }
        },
        managerData: {
            handler(newVal) {
                if (newVal && Object.keys(newVal).length > 0) {
                    this.initializeForm();
                }
            },
            deep: true
        }
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
    },

    methods: {
        addEscapeKeyListener() {
            document.addEventListener('keydown', this.handleEscapeKey);
        },

        removeEscapeKeyListener() {
            document.removeEventListener('keydown', this.handleEscapeKey);
        },

        // ESC 키 처리
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.show) {
                this.closeModal();
            }
        },

        initializeForm() {
            if (this.isEdit && this.managerData) {
                this.formData = {
                    id: this.managerData.id,
                    userId: this.managerData.userId || '',
                    password: '', // 보안상 비밀번호는 표시하지 않음
                    name: this.managerData.name || '',
                    department: this.managerData.department || '',
                    email: this.managerData.email || '',
                    phone: this.managerData.phone || '',
                    bankInfo: this.managerData.bankInfo || '',
                    bank_name: this.managerData.bank_name || '',
                    bank_number: this.managerData.bank_number || '',
                    bank_username: this.managerData.bank_username || ''
                };
            } else {
                this.formData = {
                    id: null,
                    userId: '',
                    password: '',
                    name: '',
                    department: '',
                    email: '',
                    phone: '',
                    bankInfo: '',
                    bank_name: '',
                    bank_number: '',
                    bank_username: ''
                };
            }
        },

        validateForm() {
            if (!this.formData.userId?.trim()) {
                this.$swal.fire('오류', '담당자 아이디를 입력해주세요.', 'error');
                return false;
            }

            if (!this.isEdit && !this.formData.password?.trim()) {
                this.$swal.fire('오류', '담당자 비밀번호를 입력해주세요.', 'error');
                return false;
            }

            if (!this.formData.name?.trim()) {
                this.$swal.fire('오류', '담당자명을 입력해주세요.', 'error');
                return false;
            }

            return true;
        },

        async saveManager() {
            if (!this.validateForm()) {
                return;
            }

            const formData = {
                userId: this.formData.userId.trim(),
                password: this.formData.password, // 비밀번호가 비어있어도 전송 (수정 시 선택적)
                name: this.formData.name.trim(),
                department: this.formData.department,
                email: this.formData.email.trim(),
                phone: this.formData.phone.trim(),
                bankInfo: this.formData.bankInfo.trim(),
                bank_name: this.formData.bank_name.trim(),
                bank_number: this.formData.bank_number.trim(),
                bank_username: this.formData.bank_username.trim()
            };

            if (this.isEdit) {
                formData.id = this.formData.id;
            }

            try {
                const companykey = this.$globalState.company_key;
                if (!companykey) {
                    this.$swal.fire('오류', '회사 정보를 찾을 수 없습니다.', 'error');
                    return;
                }

                let response;
                if (this.isEdit) {
                    // 수정 - 비밀번호 포함하여 전송
                    response = await this.$axios({
                        method: 'put',
                        url: `${this.$apiUrls.hanqmedi}/updateManager/${formData.id}`,
                        data: {
                            userId: formData.userId,
                            password: formData.password, // 비밀번호 포함
                            name: formData.name,
                            department: formData.department,
                            email: formData.email,
                            phone: formData.phone,
                            bankInfo: formData.bankInfo,
                            bank_name: formData.bank_name,
                            bank_number: formData.bank_number,
                            bank_username: formData.bank_username
                        }
                    });

                    console.log(response.data);
                } else {
                    // 추가
                    response = await this.$axios({
                        method: 'post',
                        url: `${this.$apiUrls.hanqmedi}/addManager`,
                        data: {
                            ...formData,
                            companykey
                        }
                    });
                }

                if (response.data.success) {
                    this.$swal.fire('성공', response.data.message, 'success');

                    // 부모 컴포넌트에 저장 완료 알림
                    this.$emit('save', formData);

                    if (this.isEdit) {
                        // 수정 시: 폼 데이터 업데이트하여 화면에 반영
                        this.formData = {
                            ...this.formData,
                            userId: formData.userId,
                            name: formData.name,
                            department: formData.department,
                            email: formData.email,
                            phone: formData.phone,
                            bankInfo: formData.bankInfo,
                            bank_name: formData.bank_name,
                            bank_number: formData.bank_number,
                            bank_username: formData.bank_username
                            // 비밀번호는 보안상 업데이트하지 않음
                        };
                    } else {
                        // 추가 시: 모달 닫기
                        this.closeModal();
                    }
                } else {
                    this.$swal.fire('오류', response.data.message || '담당자 저장에 실패했습니다.', 'error');
                }
            } catch (error) {
                console.error('담당자 저장 오류:', error);
                this.$swal.fire('오류', '담당자 저장 중 오류가 발생했습니다.', 'error');
            }
        },

        // 담당자 삭제
        async deleteManager() {
            try {
                const result = await this.$swal.fire({
                    title: '담당자 삭제',
                    text: `${this.formData.name} 담당자를 삭제하시겠습니까?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소'
                });

                if (result.isConfirmed) {
                    const response = await this.$axios({
                        method: 'delete',
                        url: `${this.$apiUrls.hanqmedi}/deleteManager/${this.formData.id}`
                    });

                    if (response.data.success) {
                        this.$swal.fire('삭제 완료', response.data.message, 'success');

                        // 부모 컴포넌트에 삭제 완료 알림
                        this.$emit('delete', this.formData.id);

                        // 모달 닫기
                        this.closeModal();
                    } else {
                        this.$swal.fire('오류', response.data.message || '담당자 삭제에 실패했습니다.', 'error');
                    }
                }
            } catch (error) {
                console.error('담당자 삭제 오류:', error);
                this.$swal.fire('오류', '담당자 삭제 중 오류가 발생했습니다.', 'error');
            }
        },

        closeModal() {
            this.removeEscapeKeyListener();
            this.$emit('close');
        },
    },

    beforeUnmount() {
        this.removeEscapeKeyListener();
    }
};
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>